import React, { useState } from 'react';
import LomavisCard1 from '../../../generic/cards/LomavisCard1/LomavisCard1';
import LomavisButtonToggle1 from '../../../generic/buttons/LomavisButtonToggle1';
import { useTranslate } from '@tolgee/react';
import { useDispatch, useSelector } from 'react-redux';
import { setApprovalConfiguration } from '../../../../redux/slices/LomavisPostApproval/lomavisPostApproval';
import {
  APPROVAL_DECISIONS, STATUS_ACTIVE,
  STEP_MODE_MULTI_STEP,
  STEP_MODE_SINGLE_STEP,
  TARGET_TEXT_PUBLISH_WITH_SCHEDULE,
  TARGET_TEXT_SAVE_AS_APPROVED_POST
} from '../../../../redux/slices/LomavisPostApproval/constants';
import StatusHeaderTag from './cards/statusHeaderTag';
import CheckStepStatus from './cards/checkStepStatus';
import { formatDate } from '../../../../utils/dateUtils';
import { getDateTimeFormated } from '../../../utils/dates/utils';
import UserInfoTimelineCard from './cards/userInfoTimelineCard';
import { formatUsersForTimeline } from '../userSelectTable/utils';
import { PLATFORM_STATIC_ICONS } from '../../../../config/icons';
import './index.css';

const CurrentStatusCard: React.FC = () => {
  const dispatch = useDispatch();
  const { t } = useTranslate();
  const {
    data: {
      step_mode,
      status,
      created,
      target,
      approval_step_1_users_rule,
      approval_step_2_users_rule,
      approval_step_1_users,
      approval_step_2_users,
      approval_step_2_group,
      approval_step_1_group,
      approval_step_1_final_decision,
      approval_step_2_final_decision,
      approval_step_1_decision_timestamp,
      approval_step_2_decision_timestamp,
      approval_process_final_decision,
      approval_process_started_timestamp,

      location_uuid,
      cloud_uuid,
      is_multipost
    }
  } = useSelector((state: any) => state.lomavisPostApproval);
  const onFlowChange = (value: string) => {
    dispatch(setApprovalConfiguration({ key: 'step_mode', value }));
  };

  const checkAllUserApproval = ({ users, rule }) => {
    const approvals = users.map((user) => user.isApproved);
    const allApproved = approvals.every((approved) => approved === true);
    const atLeastOneApproved = approvals.some((approved) => approved === true);
    const allRejected = approvals.every((approved) => approved === false);
    const allNull = approvals.every((approved) => approved === null);
    const hasNull = approvals.includes(null);

    if (allNull) return 'empty';

    if (rule === 1) {
      if (allApproved) return 'approved';
      if (approvals.includes(false)) return 'rejected';
      return 'empty'; // Some users haven't decided, but no one has rejected
    }

    if (rule === 2) {
      if (atLeastOneApproved) return 'approved';
      if (hasNull) return 'empty'; // Decision is pending
      return 'rejected'; // Only return rejected when all users disapprove
    }

    return 'empty'; // Fallback case (shouldn't be reached)
  };

  const checkDecision = (decision: boolean | null) => {
    if (decision === true) {
      return APPROVAL_DECISIONS.APPROVED;
    } else if (decision === false) {
      return APPROVAL_DECISIONS.REJECTED;
    } else {
      return APPROVAL_DECISIONS.EMPTY;
    }
  };

  return (
    <LomavisCard1
        additionalClassName={"d-flex flex-column flex-grow-1"}
        title={t('lomavisPostApproval.currentStatus.title')}
        helpText={t('lomavisPostApproval.currentStatus.helpText')}
    >
      <div className="d-flex flex-column flex-grow-1 justify-content-between timeline timeline-border-dashed  mt-4 w-100">
        <div className="d-flex flex-column flex-grow-1 timeline-item pb-4">
          <div className="timeline-line "></div>

          <div className=" me-4 z-1 timeline-sub">
            <StatusHeaderTag
              status={status >= STATUS_ACTIVE ? 'approved' : 'empty'}
              title={status >= STATUS_ACTIVE ? t('lomavisPostApproval.sendForApproval.title'): t('lomavisPostApproval.sendForApproval.notStarted')}
              dateTime={
                approval_process_started_timestamp &&
                getDateTimeFormated(approval_process_started_timestamp)
              }
            />
          </div>
        </div>

        {status > 1 &&
          (approval_step_1_users?.length > 0 || approval_step_1_group) && (
            <div className="d-flex flex-column flex-grow-1 timeline-item w-100">
              <div className="timeline-line"></div>

              <div className="me-4 w-100 timeline-sub">
                <StatusHeaderTag
                  status={checkDecision(approval_step_1_final_decision)}
                  title={t('lomavisPostApproval.feedback1.title')}
                  rule={approval_step_1_users_rule}
                  dateTime={
                    approval_step_1_decision_timestamp &&
                    getDateTimeFormated(approval_step_1_decision_timestamp)
                  }
                  group={approval_step_1_group}
                  users={formatUsersForTimeline(
                    approval_step_1_users,
                    t('role'),
                    {
                      location_uuid,
                      cloud_uuid,
                      is_multipost
                    }
                  )}
                />
              </div>
            </div>
          )}

        {status > 1 &&
          (approval_step_2_users?.length > 0 || approval_step_2_group) && (
            <div className="d-flex flex-column flex-grow-1 timeline-item w-100">
              <div className="timeline-line "></div>

              <div className="me-4 w-100 timeline-sub">
                <StatusHeaderTag
                  status={checkDecision(approval_step_2_final_decision)}
                  title={t('lomavisPostApproval.feedback2.title')}
                  rule={approval_step_2_users_rule}
                  dateTime={
                    approval_step_2_decision_timestamp &&
                    getDateTimeFormated(approval_step_2_decision_timestamp)
                  }
                  group={approval_step_2_group}
                  users={formatUsersForTimeline(
                    approval_step_2_users,
                    t('role'),
                    {
                      location_uuid,
                      cloud_uuid,
                      is_multipost
                    }
                  )}
                />
              </div>
            </div>
          )}

        {status > 1 && (
          <div className="d-flex flex-column timeline-item  timeline-border-dashed pb-4">
            <div className="timeline-line"></div>

            <div className="me-4 timeline-sub">
              <StatusHeaderTag
                status={checkDecision(approval_process_final_decision)}
                title={
                  target === 1
                    ? t(TARGET_TEXT_SAVE_AS_APPROVED_POST)
                    : t(TARGET_TEXT_PUBLISH_WITH_SCHEDULE)
                }
              />
            </div>
          </div>
        )}
      </div>
    </LomavisCard1>
  );
};

export default CurrentStatusCard;
